import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem,
  Paper, Grid, CircularProgress, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider,
} from '@mui/material';
import { ArrowBack, ExitToApp, LocalHospital, Refresh } from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { authService, salarySlipService, expenseService } from '../../../services/api';

const MONTHS = [
  'Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec',
];
const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const PIE_COLORS = [
  '#c62828','#1565c0','#e65100','#f9a825','#0277bd',
  '#6a1b9a','#4e342e','#37474f','#2e7d32','#546e7a',
];

const fmt2 = n => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtShort = n => {
  const v = Number(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const StatCard = ({ label, value, color, sub }) => (
  <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${color}`, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5 }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

export default function PayrollExpenseDashboard() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);

  // Monthly data
  const [slips,        setSlips]        = useState([]);
  const [expSummary,   setExpSummary]   = useState({ summary: [], grandTotal: 0 });

  // Trend data (full year)
  const [payrollTrend, setPayrollTrend] = useState([]);
  const [expenseTrend, setExpenseTrend] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, ptRes, etRes] = await Promise.all([
        salarySlipService.getAll({ month, year }),
        expenseService.getSummary({ month, year }),
        salarySlipService.getAll({ year }),           // all months this year for trend
        expenseService.getMonthlyTrend(year),
      ]);
      setSlips(sRes.data);
      setExpSummary(eRes.data);

      // Build payroll trend: group by month
      const payMap = {};
      sRes.data && (await salarySlipService.getAll({ year })).data.forEach(s => {
        payMap[s.month] = (payMap[s.month] || 0) + (s.netSalary || 0);
      });
      // Use ptRes for yearly payroll
      const payMapYearly = {};
      ptRes.data.forEach(s => {
        payMapYearly[s.month] = (payMapYearly[s.month] || 0) + (s.netSalary || 0);
      });
      setPayrollTrend(
        MONTHS.map((m, i) => ({
          month: m,
          payroll: Math.round(payMapYearly[i + 1] || 0),
        }))
      );

      // Expense trend
      const expMap = {};
      etRes.data.forEach(e => { expMap[e.month] = e.total; });
      setExpenseTrend(
        MONTHS.map((m, i) => ({
          month: m,
          expenses: Math.round(expMap[i + 1] || 0),
        }))
      );
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  // Derived monthly figures
  const totalPayroll   = slips.reduce((s, x) => s + (x.netSalary || 0), 0);
  const totalExpenses  = expSummary.grandTotal || 0;
  const totalCost      = totalPayroll + totalExpenses;
  const paidSlips      = slips.filter(s => s.isPaid).length;

  // Combined trend for line chart
  const combinedTrend = MONTHS.map((m, i) => ({
    month: m,
    payroll:  payrollTrend[i]?.payroll  || 0,
    expenses: expenseTrend[i]?.expenses || 0,
    total:    (payrollTrend[i]?.payroll || 0) + (expenseTrend[i]?.expenses || 0),
  }));

  // Pie data for expense categories
  const expPieData = expSummary.summary.map(s => ({ name: s.category, value: Math.round(s.total) }));

  // Bar data: payroll vs expenses per month
  const barData = combinedTrend;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Payroll & Expense Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>

        {/* Month / Year selector */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {MONTHS_FULL.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Showing {MONTHS_FULL[month - 1]} {year} details with full-year trends
            </Typography>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── KPI cards ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label={`Net Payroll — ${MONTHS_FULL[month - 1]}`}
                  value={fmtShort(totalPayroll)}
                  color="#1565c0"
                  sub={`${paidSlips} / ${slips.length} staff paid`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label={`Other Expenses — ${MONTHS_FULL[month - 1]}`}
                  value={fmtShort(totalExpenses)}
                  color="#c62828"
                  sub={`${expSummary.summary?.length || 0} categories`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total Operating Cost"
                  value={fmtShort(totalCost)}
                  color="#283593"
                  sub="Payroll + Expenses"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Expense Share"
                  value={totalCost > 0 ? `${Math.round((totalExpenses / totalCost) * 100)}%` : '—'}
                  color="#e65100"
                  sub="Expenses as % of total cost"
                />
              </Grid>
            </Grid>

            {/* ── Charts row ── */}
            <Grid container spacing={3} sx={{ mb: 3 }}>

              {/* Monthly Payroll vs Expenses bar chart */}
              <Grid item xs={12} md={8}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Monthly Payroll vs Expenses — {year}
                  </Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={barData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={v => fmtShort(v)} />
                      <Tooltip formatter={(v, n) => [fmt2(v), n === 'payroll' ? 'Payroll' : 'Expenses']} />
                      <Legend />
                      <Bar dataKey="payroll"  name="Payroll"  fill="#1565c0" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="expenses" name="Expenses" fill="#c62828" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Expense category pie */}
              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Expense Breakdown — {MONTHS_FULL[month - 1]}
                  </Typography>
                  {expPieData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={expPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${Math.round(percent * 100)}%`}
                            labelLine={false}
                          >
                            {expPieData.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={v => fmt2(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 1 }}>
                        {expPieData.map((d, i) => (
                          <Box key={d.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                              <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>{d.name}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{fmtShort(d.value)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">No expenses recorded this month.</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* ── Total cost trend line chart ── */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Total Operating Cost Trend — {year}
              </Typography>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={combinedTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={v => fmtShort(v)} />
                  <Tooltip formatter={(v, n) => [fmt2(v), n === 'total' ? 'Total Cost' : n === 'payroll' ? 'Payroll' : 'Expenses']} />
                  <Legend />
                  <Line type="monotone" dataKey="payroll"  name="Payroll"    stroke="#1565c0" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="expenses" name="Expenses"   stroke="#c62828" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="total"    name="Total Cost" stroke="#283593" strokeWidth={2.5} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            {/* ── Staff payroll breakdown table ── */}
            {slips.length > 0 && (
              <Paper elevation={3} sx={{ borderRadius: 2, mb: 3 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Staff Payroll — {MONTHS_FULL[month - 1]} {year}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Staff</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Present</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Absent</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Gross (₹)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Deductions (₹)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Net (₹)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {slips.map(s => (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{s.userName}</TableCell>
                          <TableCell><Chip label={s.role} size="small" variant="outlined" /></TableCell>
                          <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>{s.presentDays}</TableCell>
                          <TableCell align="center" sx={{ color: 'error.main', fontWeight: 600 }}>{s.absentDays}</TableCell>
                          <TableCell align="right">{fmt2(s.grossSalary)}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {fmt2((s.absenceDeduction || 0) + (s.fixedDeductions || 0))}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>{fmt2(s.netSalary)}</TableCell>
                          <TableCell align="center">
                            <Chip label={s.isPaid ? 'Paid' : 'Pending'} color={s.isPaid ? 'success' : 'warning'} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                        <TableCell colSpan={6} sx={{ fontWeight: 700 }}>Total Net Payroll</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark', fontSize: '1rem' }}>
                          {fmt2(totalPayroll)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* ── Expense category breakdown table ── */}
            {expSummary.summary?.length > 0 && (
              <Paper elevation={3} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Expense Categories — {MONTHS_FULL[month - 1]} {year}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Entries</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount (₹)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>% of Expenses</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expSummary.summary.map(s => (
                        <TableRow key={s.category} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{s.category}</TableCell>
                          <TableCell align="center">{s.count}</TableCell>
                          <TableCell align="right">{fmt2(s.total)}</TableCell>
                          <TableCell align="right">
                            {totalExpenses > 0 ? `${((s.total / totalExpenses) * 100).toFixed(1)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#ffebee' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Other Expenses</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'error.dark', fontSize: '1rem' }}>
                          {fmt2(totalExpenses)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', bgcolor: '#e8eaf6' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#283593' }}>
                    Total Operating Cost (Payroll + Expenses): {fmt2(totalCost)}
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>
    </div>
  );
}
